import { useFormContext } from '@ariakit/react'
import { isEmpty } from 'lodash-es'
import { useContext } from 'react'
import styled from 'styled-components'

import { FormError } from '../../../../../app/_components/form/FormError'
import { VariantSelectionColorRadioGroup } from '../../../../../app/_components/forms/VariantSelectionColorField'
import {
  VariantSelectionFitRadioGroup,
  useVariantSelectionFitField,
} from '../../../../../app/_components/forms/VariantSelectionFitField'
import {
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormFields,
  VariantSelectionFormState,
} from '../../../../../app/_components/forms/VariantSelectionForm'
import {
  VariantSelectionSizeRadioGroup,
  useVariantSelectionSizeField,
} from '../../../../../app/_components/forms/VariantSelectionSizeField'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'

import { PanelOptionDisclosure } from './PanelOptionDisclosure'

/**
 * The garment rows of the options panel: the color and size the customer picked on the page,
 * each opening the page's own selector so a change here is a change to the page's selection.
 *
 * Renders nothing outside a product page's variant selection form, such as on a kit.
 */
export const GarmentOptionRows: React.FC = () => {
  if (useContext(VariantSelectionFormContext) === undefined) return null

  return (
    <>
      <GarmentColorRow />
      <GarmentSizeRow />
    </>
  )
}

const GarmentColorRow: React.FC = () => {
  const { product } = useContext(VariantSelectionFormContext)!
  const value = useFormContext()!.useValue<VariantSelectionFormState['color']>('color')
  const {
    embroidery: {
      optionRows: { garmentLabels },
    },
  } = useTranslation()

  const colorName = product.options.colors?.find(option => option.color.id === value)?.color
    .displayValue

  return (
    <PanelOptionDisclosure label={garmentLabels.color} subLabel={colorName}>
      <VariantSelectionColorRadioGroup priceBucketing='compact' />
    </PanelOptionDisclosure>
  )
}

const GarmentSizeRow: React.FC = () => {
  const { displayValue: sizeName } = useVariantSelectionSizeField()
  const { displayValue: fitName } = useVariantSelectionFitField()
  const {
    embroidery: {
      optionRows: { garmentLabels },
      modal: { sizeVariants },
    },
  } = useTranslation()

  const subLabel = isEmpty(sizeName)
    ? sizeVariants.sizeNotSelected
    : [sizeName, fitName].filter(name => !isEmpty(name)).join(' | ')

  return (
    <PanelOptionDisclosure
      label={garmentLabels.size}
      subLabel={subLabel}
      closedSummary={
        <>
          <FormError name='size' />
          <FormError name='fit' />
        </>
      }
    >
      <VariantSelectionFormFields>
        <VariantSelectionSizeRadioGroup hideLabel />
        <FitRadioGroup />
      </VariantSelectionFormFields>
    </PanelOptionDisclosure>
  )
}

const FitRadioGroup = styled(VariantSelectionFitRadioGroup)`
  ${VariantSelectionFieldValueDisplay} {
    font-weight: ${o => o.theme.typography.font.weight[600]};
    color: ${o => o.theme.color.text.secondary.on.subtle};
  }
`
